import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import throttle from "lodash-es/throttle";
import { getAuth, sendEmailVerification, createUserWithEmailAndPassword } from "firebase/auth";
import { AppDispatch } from "app/store";
import { parentsFirebase } from "app/Auth";
import { actions as configActions } from "app/configSlice";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import parentI18n from "features/parent/i18n";
import { updateLastSignInTime } from "features/user/api";
import { useSignUp } from "./hook";
import { MobileInput } from "./AuthStyles";

export const EmailRegisterForm = ({
  onSuccess,
  onLoadingChange,
  showSuccessToast = false,
  keepLoadingOnSuccess = false,
}: {
  onSuccess?: () => void | Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
  showSuccessToast?: boolean;
  keepLoadingOnSuccess?: boolean;
}) => {
  useSignUp();
  const dispatch = useDispatch<AppDispatch>();
  const playSound = usePlaySound();
  const auth = getAuth(parentsFirebase);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const registerRef = useRef({ email, password, confirmPassword });
  registerRef.current = { email, password, confirmPassword };

  const setLoading = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);
      onLoadingChange?.(loading);
    },
    [onLoadingChange],
  );

  const handleRegister = useMemo(
    () =>
      throttle(
        async () => {
          const { email, password, confirmPassword } = registerRef.current;
          setLoading(true);
          playSound();

          if (!password || !confirmPassword) {
            setError(parentI18n.t("MSG_SIGN_PASSWORD_REQUIRED"));
            setLoading(false);
            return;
          }

          if (password !== confirmPassword) {
            setError(parentI18n.t("MSG_SIGN_PASSWORD_MISMATCH"));
            setLoading(false);
            return;
          }

          setError("");

          try {
            const actionCodeSettings = {
              url: Constants.publicUrl,
              handleCodeInApp: false,
            };
            await createUserWithEmailAndPassword(auth, email, password);
            updateLastSignInTime();
            dispatch(configActions.setJustAuthenticated(true));
            if (auth.currentUser) {
              sendEmailVerification(auth.currentUser, actionCodeSettings).catch(console.error);
            }
            if (showSuccessToast) {
              toast(
                <div>
                  <p className="text text-textcolor-black">
                    {parentI18n.t("MSG_SIGN_UP_SUCCESS_CONTENT")}
                  </p>
                </div>,
              );
            }
            await onSuccess?.();
            // ダイアログ版（keepLoadingOnSuccess）は認証成功後もダイアログが閉じる
            // まで AuthProvider がローディングを担うため、ここでは解除しない。
            if (!keepLoadingOnSuccess) {
              setLoading(false);
            }
          } catch (err) {
            console.error(err);
            const code = (err as { code?: string })?.code;
            if (code === "auth/email-already-in-use") {
              setError(parentI18n.t("MSG_SIGN_UP_EMAIL_ALREADY_EXISTS_FAILED_MESSAGE"));
            } else {
              setError(parentI18n.t("MSG_SIGN_UP_FAILED"));
            }
            setLoading(false);
          }
        },
        3000,
        { leading: true, trailing: false },
      ),
    [auth, dispatch, onSuccess, playSound, setLoading, showSuccessToast, keepLoadingOnSuccess],
  );

  useEffect(() => () => handleRegister.cancel(), [handleRegister]);

  return (
    <div className="flex-col-el flex-center w-full gap-4">
      <MobileInput
        type="email"
        value={email}
        onChange={(e) => {
          setError("");
          setEmail(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
        label={parentI18n.t("MSG_SIGN_USER_MAIL")}
        disabled={isLoading}
      />

      <MobileInput
        type="password"
        value={password}
        onChange={(e) => {
          setError("");
          setPassword(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
        label={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
        disabled={isLoading}
      />

      <MobileInput
        type="password"
        value={confirmPassword}
        onChange={(e) => {
          setError("");
          setConfirmPassword(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD_CONFIRM")}
        label={parentI18n.t("MSG_SIGN_USER_PASSWORD_CONFIRM")}
        disabled={isLoading}
      />

      {error && <p className="type-text type-sm text-danger text-center">{error}</p>}

      <button
        type="button"
        className="w-[375px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
        onClick={handleRegister}
        disabled={isLoading}
      >
        {parentI18n.t("MSG_SIGN_UP_BTN")}
      </button>
    </div>
  );
};
